"use client";

import { cn } from "@/lib/utils";
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { useSidebar } from "@/contexts/SidebarContext.hook";
import { useScreenSize } from "@/hooks/use-mobile";
// Re-export for convenience
// eslint-disable-next-line react-refresh/only-export-components
export { useSidebar };

interface Links {
  label: string;
  href: string;
  icon: React.JSX.Element | React.ReactNode;
}

export const AnimatedSidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
  const { className, children } = props;
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar className={className as string | undefined}>
        {children as React.ReactNode}
      </MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate } = useSidebar();
  const { isMobile } = useScreenSize();

  return (
    <motion.div
      className={cn(
        "fixed left-0 top-0 h-screen px-4 py-4 hidden md:flex md:flex-col bg-sidebar border-r border-sidebar-border z-50",
        className
      )}
      initial={{
        width: "60px"
      }}
      animate={{
        width: animate ? (open ? "300px" : "60px") : "300px",
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
      onMouseEnter={() => !isMobile && setOpen(true)}
      onMouseLeave={() => !isMobile && setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) => {
  const { open, setOpen } = useSidebar();

  // Note: Auto-close on route change is handled by individual navigation components
  // Header mobile agora é tratado pelo MobileHeader component separadamente

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "-100%", opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className={cn(
            "fixed h-screen w-full inset-0 bg-sidebar text-sidebar-foreground p-6 z-60 flex flex-col justify-between overflow-y-auto md:hidden",
            className
          )}
          {...props}
        >
          <div
            className="absolute right-6 top-6 z-50 text-sidebar-foreground cursor-pointer p-2 hover:bg-sidebar-accent rounded-md transition-colors"
            onClick={() => setOpen(false)}
          >
            <X className="h-5 w-5" />
          </div>
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const SidebarLink = ({
  link,
  className,
  onClick,
  ...props
}: {
  link: Links;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  props?: React.ComponentProps<"a">;
}) => {
  const { open, animate } = useSidebar();

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
    } else if (link.href && link.href !== '#') {
      // Handle React Router navigation
      e.preventDefault();
      window.location.href = link.href;
    }
  };

  return (
    <a
      href={link.href}
      className={cn(
        "flex items-center justify-start gap-2 group/sidebar py-1.5 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground rounded-md px-2 cursor-pointer transition-colors",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      <div className="shrink-0">
        {link.icon}
      </div>
      <motion.span
        animate={{
          opacity: animate ? (open ? 1 : 0) : 1,
          width: animate ? (open ? "auto" : 0) : "auto",
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        className="text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-nowrap overflow-hidden p-0! m-0!"
      >
        {link.label}
      </motion.span>
    </a>
  );
};